import { supabase } from '@/lib/supabaseClient';
import {
  fetchSettingsByKey,
  upsertSettingsByKey,
  loadAllSettings,
} from '@/lib/settingsData';
import { formatUsd, parseContractorDate } from '@/lib/jobsData';
import {
  uploadPortalPhoto,
  deletePortalPhoto,
  isStorageUrl,
  resolvePortalPhotos,
} from '@/lib/portalStorage';

export { formatUsd };

/* ----------------------------------------------------------------------------
 * Settings keys
 * ------------------------------------------------------------------------- */
export const CREW_PORTAL_KEYS = {
  PORTAL_ROSTER: 'crew_portals',
  MESSAGES: 'crew_portal_messages',
  PHOTOS: 'crew_portal_photos',
};

export const CREW_ACCESS_LEVELS = ['full', 'clock'];

/* ----------------------------------------------------------------------------
 * Code generation (6 digit unique)
 * ------------------------------------------------------------------------- */
/**
 * Cryptographically-strong numeric code generator (rejection-sampled to avoid
 * modulo bias). Replaces the old Math.random() generator. Crew codes stay
 * 6 digits to match the server-authoritative issuer (sql/008 admin RPC) and
 * the portal input; brute force is bounded by the per-IP lockout in sql/019.
 */
const genNumericCode = (len) => {
  const c =
    typeof crypto !== 'undefined' && crypto.getRandomValues ? crypto : null;
  let out = '';
  if (c) {
    while (out.length < len) {
      const buf = new Uint8Array(len);
      c.getRandomValues(buf);
      for (let i = 0; i < buf.length && out.length < len; i++) {
        // Reject 250–255 so the remaining 0–249 map uniformly onto 0–9.
        if (buf[i] < 250) out += String(buf[i] % 10);
      }
    }
  } else {
    for (let i = 0; i < len; i++) out += String(Math.floor(Math.random() * 10));
  }
  return out;
};

const gen6DigitCode = () => genNumericCode(6);

const genId = (prefix) => {
  const id =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID().split('-')[0].toUpperCase()
      : Math.random().toString(36).slice(2, 8).toUpperCase();
  return `${prefix}-${id}`;
};

/* ----------------------------------------------------------------------------
 * Loaders — leverages existing settings.crew_rates as the canonical roster
 * ------------------------------------------------------------------------- */
export const fetchPortalRoster = async () =>
  (await fetchSettingsByKey(CREW_PORTAL_KEYS.PORTAL_ROSTER, {})).value || {};

export const fetchCrewMessages = async () =>
  (await fetchSettingsByKey(CREW_PORTAL_KEYS.MESSAGES, {})).value || {};

export const fetchCrewPhotos = async () =>
  (await fetchSettingsByKey(CREW_PORTAL_KEYS.PHOTOS, {})).value || {};

export const fetchTimeEntriesForCrew = async () => {
  const { data, error } = await supabase
    .from('time_entries')
    .select('id,crew_id,name,job,date,hours,cost,created_at')
    .order('date', { ascending: false });
  if (error) throw error;
  return data || [];
};

export const fetchAssignmentsForCrew = async () => {
  const { data, error } = await supabase
    .from('schedule')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) {
    // schedule schema varies — tolerate failure
    return [];
  }
  return data || [];
};

export const fetchActiveJobsForCrew = async () => {
  const { data, error } = await supabase
    .from('jobs')
    .select('id,name,client,address,status,phase,notes')
    .eq('status', 'active')
    .order('name', { ascending: true });
  if (error) throw error;
  return data || [];
};

export const loadAllCrewPortalContractorData = async () => {
  const [allSettings, roster, messages, photos] = await Promise.all([
    loadAllSettings(),
    fetchPortalRoster(),
    fetchCrewMessages(),
    fetchCrewPhotos(),
  ]);
  return {
    crewMembers: allSettings.crew, // canonical roster from settings.crew_rates
    roster, // portal-specific data: code, access, active per crew member
    messages,
    photos,
  };
};

/* ----------------------------------------------------------------------------
 * Code management
 * ------------------------------------------------------------------------- */
export const ensureCodesForCrew = async (crewMembers = [], existing = {}) => {
  const next = { ...existing };
  const used = new Set(
    Object.values(next).map((v) => v?.code).filter(Boolean)
  );
  let mutated = false;
  for (const c of crewMembers) {
    if (!c?.id) continue;
    if (next[c.id]?.code) continue;
    let code;
    let attempts = 0;
    do {
      code = gen6DigitCode();
      attempts += 1;
      if (attempts > 50) break;
    } while (used.has(code));
    used.add(code);
    next[c.id] = {
      crew_id: c.id,
      crew_name: c.name,
      role: c.role || '',
      rate: Number(c.rate) || 0,
      code,
      access: 'full',
      active: true,
      created_at: new Date().toISOString(),
    };
    mutated = true;
  }
  // Drop entries whose crew member no longer exists in the roster.
  for (const id of Object.keys(next)) {
    if (!crewMembers.find((c) => c.id === id)) {
      delete next[id];
      mutated = true;
    } else {
      // Refresh denormalized name/rate.
      const c = crewMembers.find((x) => x.id === id);
      if (next[id].crew_name !== c.name || next[id].rate !== c.rate) {
        next[id] = {
          ...next[id],
          crew_name: c.name,
          role: c.role || next[id].role,
          rate: Number(c.rate) || 0,
        };
        mutated = true;
      }
    }
  }
  if (mutated) await upsertSettingsByKey(CREW_PORTAL_KEYS.PORTAL_ROSTER, next);
  return next;
};

export const regenerateCrewCode = async (crewId, roster = {}) => {
  if (!crewId) throw new Error('Missing crew id');
  const used = new Set(
    Object.values(roster).map((v) => v?.code).filter(Boolean)
  );
  let code;
  do {
    code = gen6DigitCode();
  } while (used.has(code));
  const next = {
    ...roster,
    [crewId]: {
      ...(roster[crewId] || {}),
      code,
      crew_id: crewId,
      created_at: new Date().toISOString(),
      active: true,
    },
  };
  await upsertSettingsByKey(CREW_PORTAL_KEYS.PORTAL_ROSTER, next);
  return next;
};

export const setCrewActive = async (crewId, active, roster = {}) => {
  const next = {
    ...roster,
    [crewId]: { ...(roster[crewId] || {}), crew_id: crewId, active: !!active },
  };
  await upsertSettingsByKey(CREW_PORTAL_KEYS.PORTAL_ROSTER, next);
  return next;
};

export const setCrewAccess = async (crewId, access, roster = {}) => {
  if (!CREW_ACCESS_LEVELS.includes(access)) throw new Error('Invalid access');
  const next = {
    ...roster,
    [crewId]: { ...(roster[crewId] || {}), crew_id: crewId, access },
  };
  await upsertSettingsByKey(CREW_PORTAL_KEYS.PORTAL_ROSTER, next);
  return next;
};

/** Reverse-lookup a crew portal entry by code. */
export const findCrewPortalByCode = (code, roster = {}) => {
  if (!code) return null;
  const target = String(code).trim();
  for (const entry of Object.values(roster || {})) {
    if (entry?.active && String(entry?.code) === target) return entry;
  }
  return null;
};

export const buildCrewPortalLink = (code) => {
  if (typeof window === 'undefined') return `/portal/crew/${code}`;
  return `${window.location.origin}/portal/crew/${code}`;
};

/* ----------------------------------------------------------------------------
 * SMS link helpers — fixed production-domain format used when texting a new
 * crew member their portal PIN. The link format is locked by product spec to
 * the path-style route `/portal/crew/:code` on the canonical app domain.
 * ------------------------------------------------------------------------- */
export const CREW_PORTAL_SMS_BASE = 'https://app.kocoresystems.com';

/** Canonical SMS link a crew member receives. */
export const buildCrewSmsLink = (code) =>
  `${CREW_PORTAL_SMS_BASE}/portal/crew/${code}`;

/** Pre-formatted SMS body — ready to paste / open in the OS SMS app. */
export const buildCrewSmsBody = ({ crewName, code, companyName } = {}) => {
  const first = String(crewName || '').trim().split(/\s+/)[0] || 'there';
  const company = String(companyName || '').trim() || 'K.O. CO+RE Systems';
  const link = buildCrewSmsLink(code);
  return `Hi ${first}, this is ${company}. Your secure crew portal PIN is ${code}. Open: ${link}`;
};

/** `sms:` deep-link the browser opens to prefill the OS messaging app. */
export const buildCrewSmsDeepLink = ({ phone, body } = {}) => {
  const cleanedPhone = String(phone || '').replace(/[^\d+]/g, '');
  const encoded = encodeURIComponent(String(body || ''));
  // The `&body=` syntax is the cross-platform convention (iOS + Android).
  if (cleanedPhone) return `sms:${cleanedPhone}?&body=${encoded}`;
  return `sms:?&body=${encoded}`;
};

/* ----------------------------------------------------------------------------
 * Admin RPC adapters — server-authoritative crew provisioning.
 *
 * Both functions try the SECURITY DEFINER admin RPC first (preferred path,
 * because it gates on profiles.role = 'admin' and atomically updates both
 * settings('crew_rates') and settings('crew_portals')). If the RPC is not
 * installed yet (e.g. before SQL migration 008 has been applied), they fall
 * back to the legacy client-side write path so Preview / pre-migration
 * environments do not break.
 * ------------------------------------------------------------------------- */
export const provisionCrewMember = async ({
  crewId = null,
  name,
  role = 'Crew',
  rate = 0,
  access = 'full',
} = {}) => {
  if (!name || !String(name).trim()) {
    return { ok: false, error: 'name_required' };
  }
  // Preferred path: SECURITY DEFINER RPC
  try {
    const { data, error } = await supabase.rpc('admin_provision_crew_member', {
      p_name: String(name).trim(),
      p_role: String(role || 'Crew').trim(),
      p_rate: Number(rate) || 0,
      p_access: access === 'clock' ? 'clock' : 'full',
      p_crew_id: crewId || null,
    });
    if (!error && data && data.ok !== false) {
      return {
        ok: true,
        source: 'rpc',
        crew_id: data.crew_id,
        crew_name: data.crew_name,
        role: data.role,
        rate: Number(data.rate) || 0,
        access: data.access || 'full',
        code: data.code,
        link: data.link || buildCrewSmsLink(data.code),
        sms_body: data.sms_body || buildCrewSmsBody({
          crewName: data.crew_name,
          code: data.code,
        }),
      };
    }
    if (data?.ok === false) {
      return { ok: false, error: data.error || 'provision_failed' };
    }
    if (!_rpcUnavailable(error)) {
      return { ok: false, error: error?.message || 'rpc_failed' };
    }
  } catch (_) { /* fall through to legacy */ }

  // Legacy fallback — direct client-side writes (requires authenticated admin
  // session + permissive RLS on settings).
  try {
    const fallbackId =
      crewId ||
      `crew_${String(name)
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/(^_+|_+$)/g, '')}_${Math.random()
        .toString(36)
        .slice(2, 6)}`;

    // 1) Upsert into settings('crew_rates')
    const { value: ratesValue } = await fetchSettingsByKey('crew_rates', []);
    const ratesArr = Array.isArray(ratesValue) ? [...ratesValue] : [];
    const lowerName = String(name).trim().toLowerCase();
    let idx = ratesArr.findIndex(
      (r) =>
        r?.id === fallbackId ||
        (r?.name || '').toLowerCase() === lowerName
    );
    const next = {
      id: idx >= 0 ? ratesArr[idx].id || fallbackId : fallbackId,
      name: String(name).trim(),
      role: String(role || 'Crew').trim() || 'Crew',
      rate: Number(rate) || 0,
    };
    if (idx >= 0) ratesArr[idx] = next;
    else ratesArr.push(next);
    await upsertSettingsByKey('crew_rates', ratesArr);

    // 2) Ensure crew_portals entry has a PIN (reuse if present).
    const roster = await fetchPortalRoster();
    const existing = roster?.[next.id];
    const usedCodes = new Set(
      Object.values(roster || {})
        .map((v) => v?.code)
        .filter(Boolean)
    );
    let code = existing?.code && String(existing.code).length === 6
      ? existing.code
      : null;
    if (!code) {
      let attempt = 0;
      do {
        code = gen6DigitCode();
        attempt += 1;
      } while (usedCodes.has(code) && attempt < 200);
    }
    const portalEntry = {
      crew_id: next.id,
      crew_name: next.name,
      role: next.role,
      rate: next.rate,
      code,
      access: access === 'clock' ? 'clock' : 'full',
      active: existing?.active === false ? false : true,
      created_at: existing?.created_at || new Date().toISOString(),
    };
    await upsertSettingsByKey('crew_portals', {
      ...(roster || {}),
      [next.id]: portalEntry,
    });

    return {
      ok: true,
      source: 'direct',
      crew_id: next.id,
      crew_name: next.name,
      role: next.role,
      rate: next.rate,
      access: portalEntry.access,
      code,
      link: buildCrewSmsLink(code),
      sms_body: buildCrewSmsBody({ crewName: next.name, code }),
    };
  } catch (e) {
    return { ok: false, error: e?.message || 'write_failed' };
  }
};

export const rotateCrewPin = async (crewId) => {
  if (!crewId) return { ok: false, error: 'crew_id_required' };
  try {
    const { data, error } = await supabase.rpc('admin_rotate_crew_pin', {
      p_crew_id: crewId,
    });
    if (!error && data && data.ok !== false) {
      return {
        ok: true,
        source: 'rpc',
        crew_id: data.crew_id,
        crew_name: data.crew_name,
        code: data.code,
        link: data.link || buildCrewSmsLink(data.code),
        sms_body:
          data.sms_body ||
          buildCrewSmsBody({ crewName: data.crew_name, code: data.code }),
      };
    }
    if (data?.ok === false) {
      return { ok: false, error: data.error || 'rotate_failed' };
    }
    if (!_rpcUnavailable(error)) {
      return { ok: false, error: error?.message || 'rpc_failed' };
    }
  } catch (_) { /* fall through */ }

  // Legacy fallback — direct write
  try {
    const roster = await fetchPortalRoster();
    const existing = roster?.[crewId];
    if (!existing) return { ok: false, error: 'crew_not_provisioned' };
    const usedCodes = new Set(
      Object.values(roster || {})
        .map((v) => v?.code)
        .filter(Boolean)
    );
    let code;
    let attempt = 0;
    do {
      code = gen6DigitCode();
      attempt += 1;
    } while (usedCodes.has(code) && attempt < 200);
    const portalEntry = {
      ...existing,
      code,
      active: true,
      created_at: new Date().toISOString(),
    };
    await upsertSettingsByKey('crew_portals', {
      ...(roster || {}),
      [crewId]: portalEntry,
    });
    return {
      ok: true,
      source: 'direct',
      crew_id: crewId,
      crew_name: existing.crew_name || '',
      code,
      link: buildCrewSmsLink(code),
      sms_body: buildCrewSmsBody({
        crewName: existing.crew_name,
        code,
      }),
    };
  } catch (e) {
    return { ok: false, error: e?.message || 'write_failed' };
  }
};

/* ----------------------------------------------------------------------------
 * Messages
 * ------------------------------------------------------------------------- */
export const sendCrewMessage = async (
  { crewId, from, body },
  messages = {}
) => {
  if (!crewId) throw new Error('Missing crew id');
  if (!body?.trim()) throw new Error('Message body required');
  const entry = {
    id: genId('CMSG'),
    from: from === 'crew' ? 'crew' : 'contractor',
    body: body.trim(),
    sent_at: new Date().toISOString(),
    read: false,
  };
  const next = {
    ...messages,
    [crewId]: [...(messages[crewId] || []), entry],
  };
  await upsertSettingsByKey(CREW_PORTAL_KEYS.MESSAGES, next);
  return next;
};

export const markCrewMessagesRead = async (crewId, by, messages = {}) => {
  if (!crewId) return messages;
  const arr = (messages[crewId] || []).map((m) =>
    m.from !== by ? { ...m, read: true } : m
  );
  const next = { ...messages, [crewId]: arr };
  await upsertSettingsByKey(CREW_PORTAL_KEYS.MESSAGES, next);
  return next;
};

export const flattenCrewInbox = (messages = {}, roster = {}) => {
  const rows = [];
  for (const [crewId, arr] of Object.entries(messages || {})) {
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const config = roster?.[crewId];
    const last = arr[arr.length - 1];
    const unread = arr.filter((m) => m.from === 'crew' && !m.read).length;
    rows.push({
      crew_id: crewId,
      crew_name: config?.crew_name || 'Unknown crew',
      code: config?.code,
      total: arr.length,
      unread,
      last_body: last?.body || '',
      last_at: last?.sent_at || '',
      last_from: last?.from || '',
      messages: arr,
    });
  }
  rows.sort((a, b) => (b.last_at || '').localeCompare(a.last_at || ''));
  return rows;
};

/* ----------------------------------------------------------------------------
 * Photos (crew uploaded site photos)
 * ------------------------------------------------------------------------- */
export const uploadCrewPhoto = async (
  { crewId, dataUrl, jobId, jobName, caption },
  current = {}
) => {
  if (!crewId) throw new Error('Missing crew id');
  if (!dataUrl) throw new Error('Missing photo');
  const entry = {
    id: genId('CPHO'),
    url: dataUrl,
    job_id: jobId || null,
    job_name: jobName || '',
    caption: caption || '',
    uploaded_at: new Date().toISOString(),
  };
  const next = {
    ...current,
    [crewId]: [entry, ...(current[crewId] || [])],
  };
  await upsertSettingsByKey(CREW_PORTAL_KEYS.PHOTOS, next);
  return next;
};

export const deleteCrewPhoto = async ({ crewId, photoId }, current = {}) => {
  if (!crewId) throw new Error('Missing crew id');
  const arr = (current[crewId] || []).filter((p) => p.id !== photoId);
  const next = { ...current, [crewId]: arr };
  await upsertSettingsByKey(CREW_PORTAL_KEYS.PHOTOS, next);
  return next;
};

export const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (!file) return resolve(null);
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/* ----------------------------------------------------------------------------
 * Public-portal RPC adapter (mirrors clientPortalData)
 *   Prefers Postgres RPCs (SECURITY DEFINER, anon-callable). Falls back
 *   to direct table reads/writes when RPCs are unavailable, requiring a
 *   Supabase auth session for that fallback path.
 * ------------------------------------------------------------------------- */
const _rpcUnavailable = (error) => {
  if (!error) return false;
  const code = error.code || '';
  const msg = (error.message || '').toLowerCase();
  return (
    code === '42883' ||
    code === '42501' ||
    code === 'PGRST202' ||
    msg.includes('does not exist') ||
    msg.includes('not found in the schema') ||
    msg.includes('permission denied')
  );
};

export const loadCrewPortalByCode = async (code) => {
  // Try RPC first
  try {
    const { data, error } = await supabase.rpc('crew_portal_load', { p_code: code });
    if (!error && data && data.ok !== false) {
      return {
        ok: true,
        source: 'rpc',
        config: data.config || null,
        active_jobs: Array.isArray(data.active_jobs) ? data.active_jobs : [],
        time_entries: Array.isArray(data.time_entries) ? data.time_entries : [],
        messages: Array.isArray(data.messages) ? data.messages : [],
        // Resolve signed URLs when the bucket is private (sql/021 cutover);
        // no-op otherwise.
        photos: await resolvePortalPhotos(
          code,
          'crew',
          Array.isArray(data.photos) ? data.photos : []
        ),
      };
    }
    if (data?.ok === false) return { ok: false, error: data.error || 'invalid_code' };
    // Terminal on any error. We do NOT fall back to direct table reads: that
    // path re-reads `time_entries` for the whole company and bypasses the
    // server-side job-notes sanitization in sql/006_portal_rpc_hardening.sql.
    // If the RPC is missing, the portal is unavailable until 002/006 are applied.
    return {
      ok: false,
      error: _rpcUnavailable(error) ? 'portal_unavailable' : error?.message || 'rpc_failed',
    };
  } catch (e) {
    return { ok: false, error: e?.message || 'rpc_failed' };
  }
};

export const sendCrewPortalMessage = async (code, body) => {
  try {
    const { data, error } = await supabase.rpc('crew_portal_send_message', {
      p_code: code,
      p_body: body,
    });
    if (!error && data?.ok) return { ok: true, source: 'rpc', message: data.message };
    if (data?.ok === false) return { ok: false, error: data.error };
    if (!_rpcUnavailable(error)) return { ok: false, error: error?.message || 'rpc_failed' };
  } catch (e) { /* fall through */ }
  try {
    const roster = await fetchPortalRoster();
    const entry = findCrewPortalByCode(code, roster);
    if (!entry) return { ok: false, error: 'invalid_code' };
    const messages = await fetchCrewMessages();
    const next = await sendCrewMessage({ crewId: entry.crew_id, from: 'crew', body }, messages);
    const arr = next[entry.crew_id] || [];
    return { ok: true, source: 'direct', message: arr[arr.length - 1] };
  } catch (e) {
    return { ok: false, error: e?.message || 'write_failed' };
  }
};

export const logCrewPortalTime = async (
  code,
  { jobName, hours, cost, date, coId, coDesc }
) => {
  try {
    const { data, error } = await supabase.rpc('crew_portal_log_time', {
      p_code: code,
      p_job_name: jobName || '',
      p_hours: Number(hours) || 0,
      p_cost: Number(cost) || 0,
      p_date: date,
      p_co_id: coId || null,
      p_co_desc: coDesc || null,
    });
    if (!error && data?.ok) return { ok: true, source: 'rpc', time_entry: data.time_entry };
    if (data?.ok === false) return { ok: false, error: data.error };
    if (!_rpcUnavailable(error)) return { ok: false, error: error?.message || 'rpc_failed' };
  } catch (e) { /* fall through */ }
  // Fallback: direct insert (requires auth session). The crew portal view
  // already has the crew config; we inject crew_id+name from the roster lookup.
  try {
    const roster = await fetchPortalRoster();
    const entry = findCrewPortalByCode(code, roster);
    if (!entry) return { ok: false, error: 'invalid_code' };
    const { data, error } = await supabase
      .from('time_entries')
      .insert({
        crew_id: entry.crew_id,
        name: entry.crew_name,
        job: jobName || '',
        date,
        hours: Number(hours) || 0,
        cost: Number(cost) || 0,
        co_id: coId || null,
        co_desc: coDesc || null,
        created_at: new Date().toISOString(),
      })
      .select()
      .single();
    if (error) throw error;
    return { ok: true, source: 'direct', time_entry: data };
  } catch (e) {
    return { ok: false, error: e?.message || 'write_failed' };
  }
};

export const uploadCrewPortalPhoto = async (
  code,
  { file, dataUrl, jobId, jobName, caption } = {}
) => {
  // Step 1: Resolve final URL.
  // Prefer uploading the raw `file` to Supabase Storage (small payload, public URL).
  // If only a legacy `dataUrl` is passed in (older callers), pass it through directly.
  let photoUrl = dataUrl || null;
  let storagePath = null;

  if (file) {
    // Resolve crew owner segment best-effort (we may not have config yet).
    let ownerId = 'anon';
    try {
      const roster = await fetchPortalRoster();
      const entry = findCrewPortalByCode(code, roster);
      if (entry?.crew_id) ownerId = entry.crew_id;
    } catch (_) { /* tolerate */ }

    const upload = await uploadPortalPhoto(file, { scope: 'crew', ownerId });
    if (upload.ok) {
      photoUrl = upload.url;
      storagePath = upload.path;
    } else if (!dataUrl) {
      return { ok: false, error: upload.error || 'storage_upload_failed' };
    }
    // else: storage failed but we still have dataUrl fallback — proceed.
  }

  if (!photoUrl) return { ok: false, error: 'missing_photo' };

  // Step 2: Persist via RPC (anon-safe) or fallback direct write.
  try {
    const { data, error } = await supabase.rpc('crew_portal_upload_photo', {
      p_code: code,
      p_data_url: photoUrl,
      p_job_id: jobId || null,
      p_job_name: jobName || '',
      p_caption: caption || '',
    });
    if (!error && data?.ok) {
      return { ok: true, source: 'rpc', photo: data.photo, storage_path: storagePath };
    }
    if (data?.ok === false) return { ok: false, error: data.error };
    if (!_rpcUnavailable(error)) return { ok: false, error: error?.message || 'rpc_failed' };
  } catch (e) { /* fall through */ }

  try {
    const roster = await fetchPortalRoster();
    const entry = findCrewPortalByCode(code, roster);
    if (!entry) return { ok: false, error: 'invalid_code' };
    const photos = await fetchCrewPhotos();
    const next = await uploadCrewPhoto(
      { crewId: entry.crew_id, dataUrl: photoUrl, jobId, jobName, caption },
      photos
    );
    const arr = next[entry.crew_id] || [];
    return { ok: true, source: 'direct', photo: arr[0], storage_path: storagePath };
  } catch (e) {
    return { ok: false, error: e?.message || 'write_failed' };
  }
};

export const deleteCrewPortalPhoto = async (code, photoId) => {
  // First, attempt to find the photo URL so we can clean up Storage too.
  let photoUrl = null;
  try {
    const roster = await fetchPortalRoster();
    const entry = findCrewPortalByCode(code, roster);
    if (entry) {
      const photos = await fetchCrewPhotos();
      const arr = photos?.[entry.crew_id] || [];
      const found = arr.find((p) => p.id === photoId);
      if (found?.url) photoUrl = found.url;
    }
  } catch (_) { /* lookup tolerable */ }

  try {
    const { data, error } = await supabase.rpc('crew_portal_delete_photo', {
      p_code: code,
      p_photo_id: photoId,
    });
    if (!error && data?.ok) {
      // Best-effort storage cleanup — only when URL points at the bucket.
      if (photoUrl && isStorageUrl(photoUrl)) {
        deletePortalPhoto(photoUrl).catch(() => {});
      }
      return { ok: true, source: 'rpc' };
    }
    if (data?.ok === false) return { ok: false, error: data.error };
    if (!_rpcUnavailable(error)) return { ok: false, error: error?.message || 'rpc_failed' };
  } catch (e) { /* fall through */ }
  try {
    const roster = await fetchPortalRoster();
    const entry = findCrewPortalByCode(code, roster);
    if (!entry) return { ok: false, error: 'invalid_code' };
    const photos = await fetchCrewPhotos();
    await deleteCrewPhoto({ crewId: entry.crew_id, photoId }, photos);
    if (photoUrl && isStorageUrl(photoUrl)) {
      deletePortalPhoto(photoUrl).catch(() => {});
    }
    return { ok: true, source: 'direct' };
  } catch (e) {
    return { ok: false, error: e?.message || 'write_failed' };
  }
};

export const markCrewPortalRead = async (code) => {
  try {
    const { data, error } = await supabase.rpc('crew_portal_mark_read', { p_code: code });
    if (!error && data?.ok) return { ok: true, source: 'rpc' };
    if (!_rpcUnavailable(error)) return { ok: false, error: error?.message };
  } catch (e) { /* fall through */ }
  try {
    const roster = await fetchPortalRoster();
    const entry = findCrewPortalByCode(code, roster);
    if (!entry) return { ok: false, error: 'invalid_code' };
    const messages = await fetchCrewMessages();
    await markCrewMessagesRead(entry.crew_id, 'contractor', messages);
    return { ok: true, source: 'direct' };
  } catch (e) {
    return { ok: false, error: e?.message };
  }
};

/* ----------------------------------------------------------------------------
 * Hours / earnings summary for a single crew member
 * ------------------------------------------------------------------------- */
export const buildCrewHoursSummary = (crewConfig, timeEntries = []) => {
  if (!crewConfig?.crew_id) return null;
  const myName = (crewConfig.crew_name || '').trim().toLowerCase();
  const filtered = timeEntries.filter((e) => {
    if (e.crew_id && e.crew_id === crewConfig.crew_id) return true;
    return (e.name || '').trim().toLowerCase() === myName;
  });
  let totalHours = 0;
  let totalCost = 0;
  const byDate = new Map();
  for (const e of filtered) {
    const h = Number(e.hours) || 0;
    const c = Number(e.cost) || 0;
    totalHours += h;
    totalCost += c;
    const date = e.date || 'unknown';
    if (!byDate.has(date)) byDate.set(date, { date, hours: 0, cost: 0, jobs: new Set() });
    const row = byDate.get(date);
    row.hours += h;
    row.cost += c;
    if (e.job) row.jobs.add(e.job);
  }
  const days = Array.from(byDate.values()).map((r) => ({
    ...r,
    jobs: Array.from(r.jobs),
  }));
  days.sort((a, b) => {
    const da = parseContractorDate(a.date)?.getTime() ?? 0;
    const db = parseContractorDate(b.date)?.getTime() ?? 0;
    return db - da;
  });
  // Last 7 days
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let weekHours = 0;
  let weekCost = 0;
  for (const d of days) {
    const dt = parseContractorDate(d.date);
    if (!dt) continue;
    const diff = (today - dt) / (1000 * 60 * 60 * 24);
    if (diff < 7) {
      weekHours += d.hours;
      weekCost += d.cost;
    }
  }
  return { totalHours, totalCost, weekHours, weekCost, days, entries: filtered };
};

export const buildCrewSchedule = (crewConfig, assignments = []) => {
  if (!crewConfig?.crew_id) return [];
  const myName = (crewConfig.crew_name || '').trim().toLowerCase();
  const matches = assignments.filter((a) => {
    const list = Array.isArray(a?.crew) ? a.crew : Array.isArray(a?.crew_ids) ? a.crew_ids : [];
    if (list.includes(crewConfig.crew_id)) return true;
    if (list.map((s) => String(s || '').toLowerCase()).includes(myName)) return true;
    if ((a?.assigned_to || '').toLowerCase() === myName) return true;
    return false;
  });
  matches.sort((a, b) => {
    const da = parseContractorDate(a.date)?.getTime() ?? 0;
    const db = parseContractorDate(b.date)?.getTime() ?? 0;
    return da - db;
  });
  return matches;
};

/* ----------------------------------------------------------------------------
 * Sanitize job notes for crew (operational only — strip financial)
 * ------------------------------------------------------------------------- */
const FINANCIAL_TYPE_BLOCKLIST = new Set([
  'invoice',
  'invoices',
  'payment',
  'payments',
  'estimate',
  'estimates',
  'cost',
  'pricing',
  'change_order_cost',
  'rebate',
]);

const FINANCIAL_KEYWORDS = /(\$|invoice|payment|paid|deposit|balance|rebate|pricing|markup|profit|margin|cost\b)/i;

export const buildCrewJobNotes = (jobs = []) => {
  const out = [];
  for (const job of jobs) {
    const notes = Array.isArray(job?.notes) ? job.notes : [];
    for (const n of notes) {
      const t = (n?.type || '').toLowerCase();
      if (FINANCIAL_TYPE_BLOCKLIST.has(t)) continue;
      if (n?.text && FINANCIAL_KEYWORDS.test(n.text)) continue;
      out.push({
        id: n?.id || `${job.id}-${n?.date || ''}-${(n?.text || '').slice(0, 12)}`,
        job_id: job.id,
        job_name: job.name || 'Job',
        type: n?.type || 'field',
        text: n?.text || '',
        date: n?.date || '',
      });
    }
  }
  out.sort((a, b) => {
    const da = parseContractorDate(a.date)?.getTime() ?? 0;
    const db = parseContractorDate(b.date)?.getTime() ?? 0;
    return db - da;
  });
  return out;
};
